import { useState } from 'react';
import { MOONPAY_BUY_URL } from '../buyUsdc.js';
import { toast } from '../toast.js';
import { track } from '../track.js';

/**
 * Buying USDC with a bank card, in every place a person can need it: the
 * wallet, the Add funds chooser, the Getting started step, the trading ticket
 * when there is no USDC to buy with, and a line on the welcome.
 *
 * All of them go through this file, so the words are the same wherever it is
 * met. Pressing it copies the person's own Solana address, says so on the
 * screen and in a tick, and opens the provider's public page in a new tab.
 * Nothing of theirs is in the link, no key and no address, no call of ours is
 * made, and nothing passes through xchange.
 */

/** The one name for it, on every button and card. */
export const BUY_USDC_TITLE = 'Buy USDC with a bank card';

/** Who does what; the agreed sentence, wherever the card is. */
export const BUY_USDC_PROVIDER =
  'A licensed provider handles the payment; xchange is not part of it.';

/** The step that was being missed, said plainly, under the card. */
export const BUY_USDC_PASTE = 'Paste your Solana address on their page, so the USDC lands here.';

/** What the person is told the moment the page opens. */
export const BUY_USDC_COPIED = 'Your address is copied; paste it on the payment page';

/** When the device refuses the clipboard, which some in-app browsers do. */
export const BUY_USDC_COPY_FAILED = 'Could not copy your address; copy it before you pay.';

/** The one line on the welcome, where a button would be clutter. */
export const BUY_USDC_WELCOME_LINE = 'No USDC yet? Buy it with a bank card in the wallet.';

/**
 * The step itself: copy the address, say so, open the page. The clipboard is
 * written from the press, before the new tab takes the focus. The note stays
 * on the screen the person came back to, where a tick alone would be gone.
 */
export function useBuyUsdc({
  address,
  from,
  openLink,
}: {
  /** The person's own Solana address, null while the wallet loads. */
  address: string | null;
  /** Where it was pressed from, for the anonymous event. */
  from: string;
  openLink: (url: string) => void;
}): { press: () => void; note: string | null } {
  const [note, setNote] = useState<string | null>(null);
  const press = (): void => {
    track('buy_usdc_opened', { from });
    if (address === null) setNote(null);
    else
      void navigator.clipboard.writeText(address).then(
        () => {
          setNote(BUY_USDC_COPIED);
          toast.ok(BUY_USDC_COPIED);
        },
        () => {
          setNote(BUY_USDC_COPY_FAILED);
        },
      );
    openLink(MOONPAY_BUY_URL);
  };
  return { press, note };
}

/**
 * The button on its own, for the places that already say where they are: the
 * wallet, the Getting started step, the ticket with no USDC in it.
 */
export function BuyUsdcButton({
  address,
  from,
  openLink,
  className = 'buy-usdc-cta',
  testId = 'buy-usdc-cta',
}: {
  address: string | null;
  from: string;
  openLink: (url: string) => void;
  className?: string;
  testId?: string;
}) {
  const buy = useBuyUsdc({ address, from, openLink });
  return (
    <>
      <button type="button" className={className} onClick={buy.press} data-testid={testId}>
        {BUY_USDC_TITLE}
      </button>
      {buy.note !== null && (
        <p className="buy-usdc-note" data-testid={`${testId}-note`}>
          {buy.note}
        </p>
      )}
    </>
  );
}

/**
 * The whole card: the first choice in Add funds, in the browser and in the
 * Receive screen inside Telegram.
 */
export function BuyUsdc({
  address,
  copy,
  openLink,
  from,
}: {
  address: string | null;
  /** Copies and says so, the way the screen it sits on already does. */
  copy: (text: string) => void;
  openLink: (url: string) => void;
  /** Where it was pressed from, for the anonymous event. */
  from: string;
}) {
  const buy = useBuyUsdc({ address, from, openLink });
  return (
    <div className="buy-usdc" data-testid="buy-usdc">
      <div className="buy-usdc-title">{BUY_USDC_TITLE}</div>
      <p className="buy-usdc-text">{BUY_USDC_PROVIDER}</p>
      <div className="buy-usdc-actions">
        <button
          type="button"
          className="buy-usdc-btn"
          disabled={address === null}
          onClick={() => {
            if (address !== null) copy(address);
          }}
          data-testid="buy-usdc-copy"
        >
          Copy my Solana address
        </button>
        <button
          type="button"
          className="buy-usdc-btn go"
          onClick={buy.press}
          data-testid="buy-usdc-open"
        >
          Open the payment page
        </button>
      </div>
      {buy.note !== null && (
        <p className="buy-usdc-text" data-testid="buy-usdc-open-note">
          {buy.note}
        </p>
      )}
      <p className="buy-usdc-text">{BUY_USDC_PASTE}</p>
    </div>
  );
}
